<template>
  <HomeGalleryView
    v-if="!workspaceMode"
    :logged-in="loggedIn"
    @open-auth="openAuth"
    @enter-workspace="enterWorkspace"
    @logout="handleLogout"
  />

  <div v-else class="app-layout" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
    <aside v-if="!isMobile" class="sidebar">
      <div class="masthead">
        <div class="wordmark">Seele</div>
        <div class="masthead-meta">
          <span>选股工作台</span>
          <span class="dot">·</span>
          <span>{{ year }}</span>
          <span class="dot">·</span>
          <span class="version">v{{ displayVersion }}</span>
        </div>
        <button class="sidebar-toggle" @click="toggleSidebar">
          <span>{{ sidebarCollapsed ? '›' : '‹' }}</span>
        </button>
      </div>

      <div class="rule" />

      <nav class="nav-menu">
        <template v-for="item in navItems" :key="item.key || item.path">
          <!-- 单项 -->
          <router-link
            v-if="item.type === 'single'"
            :to="item.path"
            class="nav-single"
            active-class="active"
          >
            <span class="num">{{ item.number }}</span>
            <span class="single-body">
              <span class="single-title">{{ item.name }}</span>
            </span>
            <span class="indicator" />
          </router-link>

          <!-- 分组 -->
          <div
            v-else
            class="nav-group"
            :class="{ active: item.isActive, expanded: expandedGroups.has(item.key) }"
          >
            <div class="group-head" @click.stop="toggleGroup(item.key)">
              <span class="num">{{ item.groupNumber }}</span>
              <span class="group-title">{{ item.name }}</span>
              <span class="group-chevron">{{ expandedGroups.has(item.key) ? '▾' : '▸' }}</span>
            </div>
            <transition name="nav-fold">
              <div v-show="expandedGroups.has(item.key)" class="group-children">
                <router-link
                  v-for="child in item.children"
                  :key="child.path"
                  :to="child.path"
                  class="group-child"
                  active-class="active"
                >
                  <span class="child-num">{{ child.number }}</span>
                  <span class="child-title">{{ child.name }}</span>
                </router-link>
              </div>
            </transition>
          </div>
        </template>
      </nav>

    </aside>

    <main class="main-content">
      <header class="top-bar">
        <div class="breadcrumb">
          <button
            v-if="isMobile"
            class="menu-toggle"
            @click="drawerVisible = true"
          >
            <span>☰</span>
          </button>
          <span class="crumb-section">{{ currentSection }}</span>
          <span v-if="currentTitle" class="crumb-divider">/</span>
          <span class="crumb-page">{{ currentTitle }}</span>
        </div>
        <div class="top-actions">
          <button class="theme-toggle" @click="toggleTheme">
            <span v-if="theme === 'dark'">☀</span>
            <span v-else>☾</span>
          </button>
          <button class="logout-btn" @click="exitWorkspace">
            <span class="logout-text">退出工作台</span>
            <span class="logout-icon">↪</span>
          </button>
        </div>
      </header>
      <div class="page-content">
        <router-view />
      </div>
      <div class="main-footer">
        <a
          href="https://beian.miit.gov.cn/"
          target="_blank"
          rel="noopener"
        >桂ICP备2023008226号-1</a>
      </div>
    </main>

    <MobileNavDrawer v-model="drawerVisible" :items="navItems" />
    <AgentFloatingWidget />
    <ToastContainer />
  </div>

  <AuthModal v-model:visible="showAuthModal" @success="onLoginSuccess" />
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useTheme } from '@/composables/useTheme'
import { useViewport } from '@/composables/useViewport'
import { isLoggedIn, removeToken, onAuthRequired } from '@/utils/auth'
import { getItem, removeItem, setItem } from '@/utils/storage'
import { checkVersion } from '@/utils/version'
import { visitorApi } from '@/api/visitor'
import AgentFloatingWidget from '@/components/agent/AgentFloatingWidget.vue'
import ToastContainer from '@/components/common/ToastContainer.vue'
import AuthModal from '@/components/auth/AuthModal.vue'
import MobileNavDrawer from '@/components/common/MobileNavDrawer.vue'
import HomeGalleryView from '@/views/HomeGalleryView.vue'

const router = useRouter()
const route = useRoute()
const { theme, toggle: toggleTheme } = useTheme()
const { isMobile } = useViewport()

const WORKSPACE_KEY = 'workspace'
const SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed'
const EXPANDED_GROUPS_KEY = 'expanded_groups'

const workspaceMode = ref(getItem(WORKSPACE_KEY) === '1')
const sidebarCollapsed = ref(getItem(SIDEBAR_COLLAPSED_KEY) === '1')
const expandedGroups = ref(new Set(JSON.parse(getItem(EXPANDED_GROUPS_KEY) || '[]')))
const showAuthModal = ref(false)
const loggedIn = ref(isLoggedIn())
const drawerVisible = ref(false)

const year = new Date().getFullYear()
const displayVersion = process.env.VUE_APP_VERSION?.split('-')[0] || '2.3.0'

function toggleSidebar () {
  sidebarCollapsed.value = !sidebarCollapsed.value
  setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed.value ? '1' : '0')
}

function toggleGroup (groupKey) {
  const next = new Set(expandedGroups.value)
  if (next.has(groupKey)) {
    next.delete(groupKey)
  } else {
    next.add(groupKey)
  }
  expandedGroups.value = next
  setItem(EXPANDED_GROUPS_KEY, JSON.stringify([...next]))
}

router.afterEach(() => {
  drawerVisible.value = false
})

function openAuth () {
  showAuthModal.value = true
}

function onLoginSuccess () {
  loggedIn.value = true
  showAuthModal.value = false
}

function enterWorkspace () {
  workspaceMode.value = true
  setItem(WORKSPACE_KEY, '1')
  router.push('/stock-basic')
}

function exitWorkspace () {
  workspaceMode.value = false
  removeItem(WORKSPACE_KEY)
  router.push('/')
}

function handleLogout () {
  removeToken()
  loggedIn.value = false
}

onMounted(() => {
  checkVersion().catch(() => {})

  onAuthRequired(() => {
    showAuthModal.value = true
  })

  // 默认收起所有分组
  if (getItem(EXPANDED_GROUPS_KEY) === null) {
    expandedGroups.value = new Set()
  }

  // 上报访客信息
  const screenW = window.screen?.width || 0
  const screenH = window.screen?.height || 0
  visitorApi.track({
    path: window.location.href,
    screen_resolution: screenW && screenH ? `${screenW}x${screenH}` : null,
    language: navigator.language || null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    platform: navigator.platform || null,
    referrer: document.referrer || null
  }).catch(() => {})
})

const navItems = computed(() => {
  const routes = router
    .getRoutes()
    .filter((r) => r.meta?.nav)
    .sort((a, b) => (a.meta.navOrder || 99) - (b.meta.navOrder || 99))

  const groupMap = new Map()
  const primaryItems = []
  const singles = []

  for (const r of routes) {
    const number = r.meta?.hero?.number || ''
    if (r.meta.group) {
      if (!groupMap.has(r.meta.group)) {
        const group = {
          type: 'group',
          key: r.meta.group,
          name: r.meta.group,
          children: [],
          order: r.meta.navOrder || 99
        }
        groupMap.set(r.meta.group, group)
        primaryItems.push(group)
      }
      groupMap.get(r.meta.group).children.push({
        path: r.path,
        name: r.meta.title || r.name,
        number
      })
    } else {
      const item = {
        type: 'single',
        path: r.path,
        name: r.meta.title || r.name,
        number,
        order: r.meta.navOrder || 99
      }
      if (r.meta.navPlacement === 'primary') primaryItems.push(item)
      else singles.push(item)
    }
  }

  // 策略独立页可插入分组之间；其余独立页仍排在分组之后。
  const result = [...primaryItems.sort((a, b) => a.order - b.order), ...singles]

  for (const item of result) {
    if (item.type === 'group') {
      item.isActive = item.children.some((c) => c.path === route.path)
      item.groupNumber = String(item.children[0]?.number || '').split('.')[0]
    }
  }

  return result
})

const currentTitle = computed(() => route.meta?.title || '')
const currentSection = computed(() => {
  const m = route.meta || {}
  return m.group || m.section || 'Seele'
})
</script>

<style lang="scss">
html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  overflow: hidden;
}

#app {
  font-family: var(--font-body);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--text-primary);
  height: 100%;
}

.app-layout {
  display: flex;
  height: 100%;

  .sidebar {
    width: 232px;
    flex-shrink: 0;
    background: var(--bg-primary);
    border-right: 1px solid var(--rule);
    color: var(--text-primary);
    display: flex;
    flex-direction: column;
    position: relative;

    /* 报刊感的纸张暗纹 */
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      pointer-events: none;
      background-image:
        radial-gradient(rgba(255, 255, 255, 0.012) 1px, transparent 1px);
      background-size: 3px 3px;
      opacity: 0.6;
    }
  }

  .masthead {
    padding: 26px 24px 18px;
    position: relative;
    z-index: 1;

    .wordmark {
      font-family: var(--font-display);
      font-weight: 600;
      font-size: 28px;
      line-height: 1;
      letter-spacing: -0.01em;
      color: var(--text-primary);
    }

    .masthead-meta {
      margin-top: 10px;
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: var(--text-faint);
      display: flex;
      align-items: center;
      gap: 6px;

      .dot {
        opacity: 0.5;
      }

      .version {
        color: var(--accent);
        font-weight: 600;
      }
    }

    .sidebar-toggle {
      position: absolute;
      top: 50%;
      right: 12px;
      transform: translateY(-50%);
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: 1px solid var(--rule);
      border-radius: 4px;
      color: var(--text-muted);
      cursor: pointer;
      font-size: 14px;
      line-height: 1;
      padding: 0;
      transition: all 0.2s;

      &:hover {
        border-color: var(--text-faint);
        color: var(--text-secondary);
      }
    }
  }

  .rule {
    height: 1px;
    margin: 0 24px;
    background: var(--rule);
    position: relative;
    z-index: 1;
  }

  .nav-menu {
    display: flex;
    flex-direction: column;
    padding: 18px 18px 24px;
    gap: 4px;
    flex: 1;
    overflow-y: auto;
    position: relative;
    z-index: 1;
  }

  .nav-single {
    display: flex;
    align-items: baseline;
    gap: 12px;
    padding: 10px 8px;
    text-decoration: none;
    color: var(--text-secondary);
    border-radius: 4px;
    transition: color 0.18s ease, background 0.18s ease;
    position: relative;

    .num {
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.08em;
      color: var(--text-faint);
      width: 22px;
      flex-shrink: 0;
    }

    .single-body {
      display: flex;
      flex-direction: column;
      gap: 2px;
      flex: 1;
    }

    .single-title {
      font-family: var(--font-display);
      font-weight: 600;
      font-size: 14px;
      letter-spacing: 0;
      line-height: 1.3;
    }

    .indicator {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: transparent;
      transition: background 0.18s ease;
    }

    &:hover {
      color: var(--text-primary);

      .num {
        color: var(--text-secondary);
      }
    }

    &.active {
      color: var(--text-primary);

      .num {
        color: var(--accent);
      }

      .indicator {
        background: var(--accent);
        box-shadow: 0 0 0 3px var(--accent-subtle);
      }
    }
  }

  .nav-group {
    margin-top: 14px;

    &:first-child {
      margin-top: 0;
    }

    .group-head {
      display: flex;
      align-items: baseline;
      gap: 12px;
      padding: 8px 8px 6px;
      cursor: pointer;
      user-select: none;
      transition: background 0.18s ease;
      border-radius: 4px;

      &:hover {
        background: var(--bg-tertiary);
      }

      .num {
        font-family: var(--font-mono);
        font-size: 10px;
        letter-spacing: 0.08em;
        color: var(--text-faint);
        width: 22px;
        flex-shrink: 0;
      }

      .group-title {
        flex: 1;
        font-family: var(--font-display);
        font-weight: 600;
        font-size: 14px;
        letter-spacing: 0.02em;
        color: var(--text-secondary);
      }

      .group-chevron {
        font-size: 10px;
        color: var(--text-faint);
        transition: transform 0.2s ease;
      }
    }

    &.active .group-head {
      .num {
        color: var(--accent);
      }

      .group-title {
        color: var(--text-primary);
      }
    }

    .group-children {
      position: relative;
      padding-left: 30px;
      margin-top: 2px;

      &::before {
        content: '';
        position: absolute;
        left: 18px;
        top: 6px;
        bottom: 6px;
        width: 1px;
        background: var(--rule);
      }
    }

    .group-child {
      display: flex;
      align-items: baseline;
      gap: 10px;
      padding: 7px 10px;
      text-decoration: none;
      color: var(--text-muted);
      border-radius: 4px;
      transition: color 0.18s ease;
      position: relative;

      &::before {
        content: '';
        position: absolute;
        left: -12px;
        top: 50%;
        width: 8px;
        height: 1px;
        background: var(--rule);
      }

      .child-num {
        font-family: var(--font-mono);
        font-size: 9px;
        letter-spacing: 0.06em;
        color: var(--text-faint);
        flex-shrink: 0;
      }

      .child-title {
        font-family: var(--font-body);
        font-size: 13px;
        font-weight: 400;
        letter-spacing: -0.005em;
      }

      &:hover {
        color: var(--text-primary);

        .child-num {
          color: var(--text-secondary);
        }
      }

      &.active {
        color: var(--text-primary);
        background: var(--accent-subtle);

        .child-num {
          color: var(--accent);
        }

        .child-title {
          font-weight: 500;
        }
      }
    }
  }

  .nav-fold-enter-active,
  .nav-fold-leave-active {
    transition: max-height 0.25s ease, opacity 0.25s ease;
    overflow: hidden;
  }

  .nav-fold-enter-from,
  .nav-fold-leave-to {
    max-height: 0;
    opacity: 0;
  }

  .nav-fold-enter-to,
  .nav-fold-leave-from {
    max-height: 500px;
    opacity: 1;
  }

  .main-footer {
    flex-shrink: 0;
    padding: 8px 0 16px;
    text-align: center;

    a {
      font-family: var(--font-body);
      font-size: 11px;
      color: var(--text-faint);
      text-decoration: none;
      transition: color 0.2s;

      &:hover {
        color: var(--text-secondary);
      }
    }
  }

  .main-content {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    background: var(--bg-primary);
    display: flex;
    flex-direction: column;
  }

  .top-bar {
    height: 52px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 28px;
    background: var(--bg-primary);
    border-bottom: 1px solid var(--rule);
  }

  .breadcrumb {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: var(--font-mono);
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-muted);
    min-width: 0;

    .crumb-section {
      color: var(--text-faint);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .crumb-divider {
      color: var(--text-faint);
      opacity: 0.5;
      flex-shrink: 0;
    }

    .crumb-page {
      color: var(--text-secondary);
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .menu-toggle {
    background: transparent;
    border: none;
    color: var(--text-secondary);
    font-size: 18px;
    cursor: pointer;
    padding: 8px;
    margin: -8px 4px -8px -8px;
    min-height: var(--touch-target);
    min-width: var(--touch-target);
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .top-actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .theme-toggle {
    background: transparent;
    border: 1px solid var(--rule);
    border-radius: 4px;
    padding: 6px 12px;
    cursor: pointer;
    font-size: 14px;
    line-height: 1;
    color: var(--text-secondary);
    transition: all 0.2s;

    &:hover {
      border-color: var(--text-faint);
      color: var(--text-primary);
    }
  }

  .logout-btn {
    background: transparent;
    border: 1px solid var(--rule);
    border-radius: 4px;
    padding: 6px 14px;
    cursor: pointer;
    font-family: var(--font-body);
    font-size: 12px;
    color: var(--text-muted);
    transition: all 0.2s;

    &:hover {
      border-color: var(--down);
      color: var(--down);
    }

    .logout-icon {
      display: none;
    }
  }

  .page-content {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  @media (max-width: 768px) {
    .top-bar {
      padding: 0 var(--page-pad-x);
      height: 56px;
    }

    .logout-btn {
      padding: 6px 10px;

      .logout-text {
        display: none;
      }

      .logout-icon {
        display: inline;
        font-size: 16px;
      }
    }

    .theme-toggle {
      padding: 6px 10px;
    }
  }
}

@media (max-width: 1200px) {
  .app-layout {
    .sidebar {
      width: 190px;
    }

    .masthead {
      padding: 20px 16px 14px;
    }

    .nav-menu {
      padding: 14px 12px 20px;
    }

  }
}

.app-layout.sidebar-collapsed {
  .sidebar {
    width: 64px;

    .masthead {
      padding: 16px 8px;

      .wordmark {
        font-size: 18px;
        text-align: center;
      }

      .masthead-meta {
        display: none;
      }

      .sidebar-toggle {
        right: 50%;
        transform: translate(50%, -50%);
      }
    }

    .rule {
      margin: 0 12px;
    }

    .nav-menu {
      padding: 12px 8px;
    }

    .nav-single {
      justify-content: center;
      padding: 10px 4px;
      gap: 0;

      .num {
        display: none;
      }

      .single-body {
        display: none;
      }

      .indicator {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }

    .nav-group {
      display: none;
    }
  }

  .top-bar {
    padding: 0 18px;
  }
}

@media (max-width: 900px) {
  .app-layout {
    .sidebar {
      width: 64px;

      .masthead {
        padding: 16px 8px;

        .wordmark {
          font-size: 18px;
          text-align: center;
        }

        .masthead-meta {
          display: none;
        }
      }

      .rule {
        margin: 0 12px;
      }

      .nav-menu {
        padding: 12px 8px;
      }

      .nav-single {
        justify-content: center;
        padding: 10px 4px;
        gap: 0;

        .num {
          display: none;
        }

        .single-body {
          display: none;
        }

        .indicator {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
      }

      .nav-group {
        display: none;
      }

    }

    .top-bar {
      padding: 0 18px;
    }
  }
}
</style>
